import { useCallback, useMemo, useState } from "react";
import { Grid2 as Grid, Stack, Button, Typography, Chip, OutlinedInput } from "@mui/material";
import moment from "moment";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../Components/Misc/ListPageHeader";
import BoxPaper from "../../../Components/Misc/BoxPaper";
import MetadataListTextItem from "../../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { DbInvoiceExpenseInfos } from "../../../Core/Api/Types/db-invoice-expense-infos";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { DbInvoiceInfos } from "../../../Core/Api/Types/db-invoice-infos";
import FormSection from "../../../Components/Forms/FormSection/form-section";
import SellIcon from "@mui/icons-material/Sell";
import SearchIcon from "@mui/icons-material/Search";
import LocalMoviesIcon from "@mui/icons-material/LocalMovies";
import { BarChart, PieChart } from "@mui/x-charts";
import { GroupByToMap } from "../../../Utils/helpers";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import DataTable, { CellSize } from "../../../Components/Table/table";
import { routePaths } from "../../../Router/router";
import { useNavigate } from "react-router";
import TableCellMovie from "../../../Components/Table/TableCellMovie/table-cell-movie";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import BillingExpenseTable from "../Expenses/billing-expenses-table";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { useTranslation } from "react-i18next";

export type BillingDetailsProps = {
    expenses: DbInvoiceExpenseInfos[];
    invoice?: DbInvoiceInfos;
};

const BillingDetails: React.FC<BillingDetailsProps> = (props) => {
    const { t } = useTranslation();
    const { expenses, invoice, ...otherProps } = props;
    const apiGrid = useGridApiRef();
    const navigate = useNavigate();
    const [filter, setFilter] = useState("");

    const handleExport = () => {
        apiGrid.current.exportDataAsCsv({ delimiter: GlobalConst.EXPORT_DELIMITER });
    };

    const totalExpenses = useMemo(() => {
        if (expenses.length === 0) return 0;
        return expenses
            .map((x) => x.price)
            .reduce((prev, next) => prev + next)
            .toFixed(2);
    }, [expenses]);

    // Cols def
    const colsTableGroupByTags: GridColDef[] = [
        {
            field: "tag",
            headerName: t("common:tag"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => <Chip label={data.value} sx={{ mt: 0.5, mb: 0.5 }} />,
        },
        {
            field: "price",
            headerName: t("common:price"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            type: "number",
            headerAlign: "left",
            align: "left",
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {data.value.toFixed(4)} EUR
                </Typography>
            ),
        },
    ];

    // Cols def
    const colsTableGroupByTitle: GridColDef[] = [
        {
            field: "titleId",
            headerName: t("common:title"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            renderCell: (data: any) => <TableCellMovie id={data.value} name={data?.row?.titleName} />,
        },
        {
            field: "count",
            headerName: t("table:headerName.items"),
            flex: CellSize.xsmall,
            minWidth: CellSize.xsmall,
            type: "number",
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {data.value}
                </Typography>
            ),
        },
        {
            field: "metrics",
            headerName: t("table:headerName.expenses"),
            flex: CellSize.xlarge,
            minWidth: CellSize.xlarge,
            renderCell: (data: any) => {
                let compute = groupedDataPerTagByTitleId(data?.row?.titleId);

                return (
                    <BarChart
                        dataset={compute.map((x) => ({ value: x.price, tag: x.tag }))}
                        series={[{ dataKey: "value", valueFormatter: (value) => `${value} EUR` }]}
                        yAxis={[{ scaleType: "band", dataKey: "tag" }]}
                        xAxis={[{ label: "Price" }]}
                        layout="horizontal"
                        height={150}
                    />
                );
            },
        },
        {
            field: "price",
            headerName: t("common:price"),
            flex: CellSize.small,
            minWidth: CellSize.small,
            type: "number",
            headerAlign: "left",
            align: "left",
            renderCell: (data: any) => (
                <Typography component="span" fontWeight={500} color="text.primary">
                    {data.value.toFixed(4)} EUR
                </Typography>
            ),
        },
    ];

    const groupedDataPerTag = useMemo(() => {
        return GroupByToMap(expenses, (x) => x.tag);
    }, [expenses]);

    const groupedDataPerTitle = useMemo(() => {
        return GroupByToMap(
            expenses.filter((x) => x.titleId && x.titleId > 0),
            (x) => x.titleId
        );
    }, [expenses]);

    const chartData = useMemo(() => {
        const ret: { id: any; value: number; label: string }[] = [];

        groupedDataPerTag.forEach((items, key) => {
            ret.push({
                id: key,
                value: parseFloat(
                    items
                        .map((x) => x.price)
                        .reduce((prev, next) => prev + next)
                        .toFixed(2)
                ),
                label: key,
            });
        });

        return ret;
    }, [groupedDataPerTag]);

    const tableGroupedDataPerTag = useMemo(() => {
        let ret: { tag: any; price: number; count: number }[] = [];

        groupedDataPerTag.forEach((items, key) => {
            ret.push({
                tag: key,
                price: parseFloat(
                    items
                        .map((x) => x.price)
                        .reduce((prev, next) => prev + next)
                        .toFixed(2)
                ),
                count: items.length,
            });
        });

        return ret.sort((a, b) => b.price - a.price);
    }, [groupedDataPerTag]);

    const groupedDataPerTagByTitleId = useCallback(
        (titleId: number) => {
            let ret: { tag: any; price: number; count: number }[] = [];

            let exp = expenses.filter((x) => x.titleId && x.titleId === titleId);
            let grp = GroupByToMap(exp, (x) => x.tag);

            grp.forEach((items, key) => {
                ret.push({
                    tag: key,
                    price: parseFloat(
                        items
                            .map((x) => x.price)
                            .reduce((prev, next) => prev + next)
                            .toFixed(2)
                    ),
                    count: items?.length,
                });
            });

            return ret.sort((a, b) => b.price - a.price);
        },
        [expenses]
    );

    const tableGroupedDataPerTitle = useMemo(() => {
        let ret: { titleId: number; titleName: string; price: number; count: number }[] = [];

        groupedDataPerTitle.forEach((items, key) => {
            ret.push({
                titleId: key ?? 0,
                titleName: items[0].titleName ?? "",
                price: parseFloat(
                    items
                        .map((x) => x.price)
                        .reduce((prev, next) => prev + next)
                        .toFixed(2)
                ),
                count: items.length,
            });
        });

        return ret.filter((x) => x.titleId).sort((a, b) => b.price - a.price);
    }, [groupedDataPerTitle]);

    return (
        <>
            <ListPageHeader mb={2}>
                <ListPageHeaderLeft
                    title={`${invoice
                        ? t("component:listPageHeaderLeft.title.invoiceWithDate", {
                            date: moment(invoice.endDate).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS),
                        })
                        : t("component:listPageHeaderLeft.title.currentMonthDetails")
                        }`}
                />
                <ListPageHeaderRight>
                    <Button onClick={() => handleExport()} variant="contained" color="primary">
                        {t("common:export")}
                    </Button>
                </ListPageHeaderRight>
            </ListPageHeader>

            <Grid container spacing={3}>
                <Grid size={8}>
                    <Stack spacing={2}>
                        <FormSection icon={<SellIcon />} title={t("common:overview")} defaultExpanded>
                            <Grid container spacing={3}>
                                <Grid size={5} alignContent="center" alignItems="center" sx={{ pl: 2 }}>
                                    <DataTable
                                        noControls
                                        rows={tableGroupedDataPerTag}
                                        columns={colsTableGroupByTags}
                                        columnHeaderHeight={30}
                                        getRowHeight={() => "auto"}
                                        getRowId={(row) => row.tag}
                                        checkboxSelection={false}
                                        pageSize={20}
                                        disableRowSelectionOnClick
                                        pagination={false}
                                    />
                                </Grid>
                                <Grid size={7} alignContent="center" alignItems="center">
                                    <PieChart
                                        series={[
                                            {
                                                data: chartData,
                                                //arcLabel: (item) => `${item.label} (${item.value.toFixed(2)})`,
                                                arcLabelMinAngle: 45,
                                                highlightScope: { faded: "global", highlighted: "item" },
                                                faded: { innerRadius: 30, additionalRadius: -30, color: "gray" },
                                                valueFormatter: (value) =>
                                                    `${value.value} EUR (${expenses.filter((x) => x.tag === value.label).length} items)`,
                                            },
                                        ]}
                                        height={250}
                                        slotProps={{
                                            legend: {
                                                direction: "column",
                                                itemMarkWidth: 20,
                                                itemMarkHeight: 3,
                                                markGap: 5,
                                                itemGap: 10,
                                                labelStyle: {
                                                    fontSize: 12,
                                                    fill: "white",
                                                },
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </FormSection>
                        <FormSection icon={<LocalMoviesIcon />} title={t("form:section.title.titleChargedExpenses")}>
                            <Stack spacing={2}>
                                <OutlinedInput
                                    className="filters-search"
                                    placeholder={t("field:placeholder.searchForTitle")}
                                    onChange={(e) => setFilter(e.target.value)}
                                    endAdornment={<SearchIcon />}
                                />
                                <DataTable
                                    noControls
                                    rows={tableGroupedDataPerTitle.filter((x) => filter === "" || x.titleName?.includesAllWords(filter))}
                                    columns={colsTableGroupByTitle}
                                    columnHeaderHeight={30}
                                    getRowHeight={() => "auto"}
                                    getRowId={(row) => row.titleId}
                                    checkboxSelection={false}
                                    pageSize={20}
                                    disableRowSelectionOnClick
                                    pagination={true}
                                    apiRef={apiGrid}
                                />
                            </Stack>
                        </FormSection>
                        <FormSection icon={<LocalMoviesIcon />} title={t("form:section.title.expensesDetails")}>
                            <BillingExpenseTable
                                expenses={expenses}
                                currency={CurrencyTypes.EUR}
                                title={t("page:billingExpenseTable.title.currentExpenses")}
                            //onRowClick={(e) => onExpenseClick(e)}
                            />
                        </FormSection>
                    </Stack>
                </Grid>
                <Grid size={4}>
                    <BoxPaper title={t("page:boxPaper.title.subscriptionSummary")} mb={2}>
                        {expenses && expenses[0] && (
                            <Stack spacing={1}>
                                <MetadataListTextItem title={t("menu:title.billingPeriod")}>
                                    {moment.utc(expenses[0].date).format("MMMM YYYY")}
                                </MetadataListTextItem>
                            </Stack>
                        )}
                    </BoxPaper>

                    <BoxPaper title={t("page:boxPaper.title.currentBill")} mb={2}>
                        <Stack spacing={1}>
                            <MetadataListTextItem title={t("menu:title.cost")}>{totalExpenses} EUR</MetadataListTextItem>

                            <MetadataListTextItem title={t("menu:title.expensesTotalNb")}>
                                {t("menu:item.itemsWithCount", { count: expenses.length })}
                            </MetadataListTextItem>
                            <MetadataListTextItem title={t("menu:title.expensesChargedNb")}>
                                {t("menu:item.itemsWithCount", { count: expenses.filter((x) => x.price !== 0).length })}
                            </MetadataListTextItem>
                        </Stack>
                    </BoxPaper>
                </Grid>
            </Grid>
        </>
    );
};

export default BillingDetails;
